import React, { useState, useEffect } from "react";
import CustomButton from "../pages/CustomButton";
import { BASE_URL } from "../config";
import axios from "../axiosConfig";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";

export default function ExtraTeam() {
  const [states, setStates] = useState([]);
  const [managers, setManagers] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [teamStateId, setTeamStateId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [managerStateId, setManagerStateId] = useState("");

  const currentYear = new Date().getFullYear();
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const [monthYear, setMonthYear] = useState(
    `${months[new Date().getMonth()]} ${currentYear}`,
  );

  const [startDate, setStartDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [endDate, setEndDate] = useState(() => {
    const dt = new Date();
    dt.setFullYear(dt.getFullYear() + 40);
    return dt.toISOString().slice(0, 10);
  });

  const [checked, setChecked] = useState({});
  const [assignmentByEmpId, setAssignmentByEmpId] = useState({});
  const [toUnassign, setToUnassign] = useState([]);
  const [updatingUnassign, setUpdatingUnassign] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch states
  useEffect(() => {
    const getStates = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        setStates(res.data || []);
      } catch (err) {
        console.error("Error fetching states", err);
        setError("Failed to load states");
      }
    };
    getStates();
  }, []);

  // Fetch managers when team state changes
  useEffect(() => {
    if (!teamStateId) {
      setManagers([]);
      setManagerId("");
      return;
    }
    const getManagers = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/managers/${teamStateId}`, {
          headers: getAuthHeaders(),
        });
        setManagers(res.data || []);
      } catch (err) {
        console.error("Error fetching managers", err);
        setManagers([]);
      }
    };
    getManagers();
    setChecked({});
    setEmployees([]);
    setManagerId("");
    setAssignmentByEmpId({});
    setToUnassign([]);
  }, [teamStateId]);

  const loadEmployees = async () => {
    if (!managerStateId || !managerId) {
      setError("Please select Manager State and Manager");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const params = new URLSearchParams({
        state_id: managerStateId,
        monthYear,
      }).toString();
      const res = await axios.get(`${BASE_URL}/api/employees?${params}`, {
        headers: getAuthHeaders(),
      });
      const list = res.data || [];
      setEmployees(list);
      const chk = {};
      list.forEach((e) => {
        const idKey = String(e.employee_id ?? e.id ?? e.employeeId ?? "");
        if (idKey) chk[idKey] = false;
      });
      const assignMap = {};
      try {
        const assignRes = await axios.get(
          `${BASE_URL}/api/temporary-assign/${managerId}`,
          { headers: getAuthHeaders() },
        );
        const data = assignRes.data;
        let assignments = [];
        if (Array.isArray(data)) assignments = data;
        else if (Array.isArray(data.maanger_list))
          assignments = data.maanger_list;
        else if (Array.isArray(data.manager_list))
          assignments = data.manager_list;
        else {
          const arrVal = Object.values(data).find((v) => Array.isArray(v));
          if (arrVal) assignments = arrVal;
        }
        assignments.forEach((a) => {
          const empId = a.employee_id ?? a.employeeId ?? a.emp_id ?? a.id;
          const isActive = a.is_active ?? a.isActive ?? a.isactive ?? a.active;
          if (empId != null) {
            const key = String(empId);
            assignMap[key] = a;
            if (!!isActive && key in chk) chk[key] = true;
          }
        });
      } catch (assignErr) {
        console.warn("Could not fetch temporary assignments", assignErr);
      }
      setChecked(chk);
      setAssignmentByEmpId(assignMap);
      setToUnassign([]);
    } catch (err) {
      console.error("Error fetching employees", err);
      setError("Failed to load employees");
      setEmployees([]);
      setChecked({});
      setAssignmentByEmpId({});
      setToUnassign([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleEmployee = (id) => {
    const key = String(id);
    setChecked((prev) => {
      const nowChecked = !prev[key];
      const assignment = assignmentByEmpId[key];
      if (assignment && assignment.id) {
        if (!nowChecked) {
          setToUnassign((old) =>
            old.includes(assignment.id) ? old : [...old, assignment.id],
          );
        } else {
          setToUnassign((old) => old.filter((aid) => aid !== assignment.id));
        }
      }
      return { ...prev, [key]: nowChecked };
    });
  };

  const updateUnassign = async () => {
    if (toUnassign.length === 0) return;
    setError(null);
    setSuccess(null);
    setUpdatingUnassign(true);
    try {
      const payload = {
        assignment_ids: toUnassign,
        end_date: endDate,
        is_active: false,
      };
      const url = `${BASE_URL}/api/update-temporary-manager/${managerId}`;
      const res = await axios.put(url, payload, { headers: getAuthHeaders() });
      console.log("Unassign response:", res.data);
      setSuccess("Employees unassigned successfully");
      setToUnassign([]);
    } catch (err) {
      console.error("Error unassigning employees", err);
      setError("Failed to unassign employees");
    } finally {
      setUpdatingUnassign(false);
    }
  };

  const saveAssignments = async () => {
    setError(null);
    setSuccess(null);
    const mgrNumeric = Number(managerId);
    if (!managerId || Number.isNaN(mgrNumeric)) {
      setError("Invalid manager selected");
      return;
    }
    const selectedIds = Object.keys(checked).filter((id) => checked[id]);
    if (selectedIds.length === 0) {
      setError("Select at least one employee to tag");
      return;
    }
    try {
      const res = await axios.post(
        `${BASE_URL}/api/assign-temporary-manager-multiple?manager_id=${mgrNumeric}`,
        {
          employee_ids: selectedIds.map((x) => Number(x)),
          start_date: startDate,
          end_date: endDate,
        },
        { headers: getAuthHeaders() },
      );
      console.log("API Response:", res.data);
      setSuccess("Employees assigned successfully");
      setChecked({});
      setEmployees([]);
      setAssignmentByEmpId({});
      setToUnassign([]);
    } catch (err) {
      console.error("Error saving assignments", err);
      setError("Failed to save assignments");
    }
  };

  const selectedCount = Object.values(checked).filter(Boolean).length;

  /* ─── Shared select / input classes ─── */
  const fieldBase =
    "w-full border border-slate-200 bg-white/80 backdrop-blur-sm rounded-lg px-3 py-1.5 text-sm text-slate-700 shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 hover:border-blue-300 appearance-none cursor-pointer";

  const labelClass =
    "text-[10px] font-bold text-slate-400 uppercase tracking-widest";

  /* ─── Field wrapper with floating icon accent ─── */
  const FieldCard = ({ icon, label, children }) => (
    <div className="group relative bg-white/60 backdrop-blur-md border border-white/80 rounded-xl p-2.5 shadow-sm hover:shadow-md hover:border-blue-200 transition-all duration-300">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-sm leading-none">{icon}</span>
        <label className={labelClass}>{label}</label>
      </div>
      {children}
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3 sm:p-4 font-sans pb-6">
      {loading && <Loader onCancel={() => setLoading(false)} />}

      {/* ── HEADER CARD ── */}
      <div className="w-full">
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-500 rounded-2xl px-6 py-4 mb-3 shadow-xl shadow-blue-200">
          {/* decorative circles */}
          <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full" />
          <div className="absolute -bottom-8 -left-8 w-36 h-36 bg-white/10 rounded-full" />
          <div className="relative flex flex-col sm:flex-row items-center gap-2">
            <div className="bg-white/20 backdrop-blur rounded-xl p-2">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-extrabold text-white tracking-tight">
                Extra Team
              </h1>
              <p className="text-blue-100 text-xs">
                Assign & manage temporary team members
              </p>
            </div>
          </div>
        </div>

        {/* ── FILTER PANEL ── */}
        <div className="bg-white/70 backdrop-blur-xl border border-white/80 rounded-2xl shadow-xl shadow-slate-100 p-3 sm:p-4 mb-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-4 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full" />
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              Filters &amp; Configuration
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
            {/* Team State */}
            <FieldCard icon="🗺️" label="Team State">
              <div className="relative">
                <select
                  value={teamStateId}
                  onChange={(e) => setTeamStateId(e.target.value)}
                  className={fieldBase}
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </FieldCard>

            {/* Manager */}
            <FieldCard icon="👤" label="Manager">
              <div className="relative">
                <select
                  value={managerId}
                  onChange={(e) => setManagerId(e.target.value)}
                  className={fieldBase}
                >
                  <option value="">Select Manager</option>
                  {managers.map((m) => (
                    <option
                      key={m.id}
                      value={m.employee_id}
                    >{`${m.employee_name} (${m.employee_designation})`}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </FieldCard>

            {/* Manager State */}
            <FieldCard icon="📍" label="Manager State">
              <div className="relative">
                <select
                  value={managerStateId}
                  onChange={(e) => setManagerStateId(e.target.value)}
                  className={fieldBase}
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </FieldCard>

            {/* Month - Year */}
            <FieldCard icon="📅" label="Month – Year">
              <div className="relative">
                <select
                  value={monthYear}
                  onChange={(e) => setMonthYear(e.target.value)}
                  className={fieldBase}
                >
                  {months.map((m, idx) => (
                    <option key={idx} value={`${m} ${currentYear}`}>
                      {m} {currentYear}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </FieldCard>

            {/* Start Date */}
            <FieldCard icon="🟢" label="Start Date">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={fieldBase}
              />
            </FieldCard>

            {/* End Date */}
            <FieldCard icon="🔴" label="End Date">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={fieldBase}
              />
            </FieldCard>
          </div>

          {/* GO Button */}
          <div className="flex justify-center mt-4">
            <button
              type="button"
              onClick={loadEmployees}
              disabled={loading}
              className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:from-slate-400 disabled:to-slate-400 text-white font-bold px-8 py-2.5 rounded-xl shadow-lg shadow-blue-200 hover:shadow-xl hover:shadow-blue-300 transition-all duration-300 text-sm tracking-wide active:scale-95"
            >
              {loading ? (
                <>
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Loading…
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z"
                    />
                  </svg>
                  Search Employees
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── ALERTS ── */}
        {error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-3.5 mb-4 text-sm shadow-sm">
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
            {error}
          </div>
        )}
        {success && (
          <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-2xl px-5 py-3.5 mb-4 text-sm shadow-sm">
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {success}
          </div>
        )}

        {/* ── EMPLOYEE LIST ── */}
        {employees.length > 0 && (
          <div className="bg-white/70 backdrop-blur-xl border border-white/80 rounded-3xl shadow-xl shadow-slate-100 overflow-hidden">
            {/* List header */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="bg-white/10 rounded-xl p-2">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-white font-bold text-base">
                    Employee List
                  </h2>
                  <p className="text-slate-400 text-xs">
                    {employees.length} employees found
                  </p>
                </div>
              </div>
              {selectedCount > 0 && (
                <span className="inline-flex items-center gap-1.5 bg-blue-500/20 border border-blue-400/30 text-blue-200 text-xs font-semibold px-3 py-1.5 rounded-full">
                  <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                  {selectedCount} selected
                </span>
              )}
            </div>

            {/* Scrollable grid - height hugs content, scrolls only when list is large */}
            <div className="overflow-y-auto overscroll-contain w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-0 divide-x divide-y divide-slate-100">
                {employees.map((e) => {
                  const empId = e.employee_id ?? e.id ?? e.employeeId;
                  const key = String(empId);
                  const isChecked = !!checked[key];
                  const isAssigned = !!assignmentByEmpId[key];

                  return (
                    <label
                      key={key}
                      htmlFor={`emp-${key}`}
                      className={`flex items-center gap-2.5 px-4 py-2 cursor-pointer transition-all duration-200 select-none
                        ${
                          isChecked
                            ? "bg-blue-50/80 border-l-4 border-blue-500"
                            : "hover:bg-slate-50 border-l-4 border-transparent"
                        }`}
                    >
                      {/* Custom checkbox */}
                      <div className="flex-shrink-0">
                        <input
                          type="checkbox"
                          id={`emp-${key}`}
                          checked={isChecked}
                          onChange={() => toggleEmployee(empId)}
                          className="sr-only"
                        />
                        <div
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                            isChecked
                              ? "bg-blue-600 border-blue-600 shadow-sm shadow-blue-200"
                              : "bg-white border-slate-300 group-hover:border-blue-300"
                          }`}
                        >
                          {isChecked && (
                            <svg
                              className="w-2.5 h-2.5 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                      </div>

                      {/* Avatar + Info */}
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div
                          className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                            isChecked
                              ? "bg-blue-600 text-white"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {(e.employee_name ??
                            e.first_name ??
                            "?")[0].toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800 text-xs leading-tight truncate">
                            {e.employee_name ??
                              `${e.first_name ?? ""} ${e.last_name ?? ""}`}
                          </div>
                          <div className="text-[10px] text-slate-500 truncate flex items-center gap-1">
                            {e.designation ?? e.employee_designation}
                            {(e.role ?? e.employee_role) && (
                              <span className="bg-slate-100 text-slate-500 px-1 py-px rounded text-[9px]">
                                {e.role ?? e.employee_role}
                              </span>
                            )}
                            {isAssigned && (
                              <span className="bg-amber-100 text-amber-700 border border-amber-200 px-1 py-px rounded text-[9px] font-medium">
                                ✓ Assigned
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="border-t border-slate-100 bg-slate-50/80 px-4 py-2.5 flex flex-col sm:flex-row items-center justify-between gap-2">
              <div className="text-xs text-slate-500">
                Showing{" "}
                <span className="font-semibold text-slate-700">
                  {employees.length}
                </span>{" "}
                employees
                {toUnassign.length > 0 && (
                  <span className="ml-2 text-amber-600 font-medium">
                    • {toUnassign.length} to unassign
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap justify-center">
                {toUnassign.length > 0 && (
                  <button
                    type="button"
                    onClick={updateUnassign}
                    disabled={updatingUnassign}
                    className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-white font-semibold px-4 py-1.5 rounded-lg text-xs shadow-sm transition-all duration-200 active:scale-95"
                  >
                    {updatingUnassign ? (
                      <>
                        <svg
                          className="w-3.5 h-3.5 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8z"
                          />
                        </svg>
                        Updating…
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Unassign ({toUnassign.length})
                      </>
                    )}
                  </button>
                )}

                <button
                  type="button"
                  onClick={saveAssignments}
                  disabled={selectedCount === 0}
                  className="inline-flex items-center gap-1.5 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed text-white font-semibold px-5 py-1.5 rounded-lg text-xs shadow-sm hover:shadow-md transition-all duration-200 active:scale-95"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Submit {selectedCount > 0 && `(${selectedCount})`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
